import React, { useEffect, useMemo, type FormEvent, type JSX } from "react";
import { useSearchParams } from "react-router-dom";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import { createDefaultAdminListTimeRange, parseJobQueryParams } from "../../contracts/admin-route-params";
import {
  formatAdminDashboardSummary,
  pickAdminDashboardLocaleText,
} from "../../shared/locale/admin-dashboard-copy";
import { AdminLocalizedLink } from "../../shared/locale/admin-dashboard-localized-link";
import { withAdminDashboardLocaleSearchParams } from "../../shared/locale/admin-dashboard-locale-routing";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import { AdminDataTable } from "../../shared/ui/admin-data-table";
import { AdminEmptyState } from "../../shared/ui/admin-empty-state";
import { AdminErrorState } from "../../shared/ui/admin-error-state";
import { AdminFilterBar } from "../../shared/ui/admin-filter-bar";
import { AdminOperatorExplanationBlock } from "../../shared/ui/admin-operator-explanation-block";
import { AdminAiRuntimeSummaryStrip } from "../../shared/ui/admin-ai-runtime-summary-strip";
import { AdminAggregateSummaryStrip } from "../../shared/ui/admin-aggregate-summary-strip";
import { AdminSourceStatusGrid } from "../../shared/ui/admin-source-status-grid";
import {
  buildAdminStateRouteMeta,
  buildAdminStateScopeMeta,
  buildAdminStateTimeRangeMeta,
} from "../../shared/ui/admin-screen-state-meta";
import {
  AdminStatusBadge,
  toAdminFreshnessBadgeValue,
} from "../../shared/ui/admin-status-badge";
import {
  clearHiddenAdminListCursorSearchParams,
  clearExplicitAdminTimeRangeSearchParams,
  resolveExplicitAdminTimeRangeUpperBound,
  resolveAdminLiveTimeRangeRefreshSeed,
  useAdminAutoRefreshTick,
} from "../../shared/query/admin-auto-refresh";
import { AdminLiveRangeBanner } from "../../shared/ui/admin-live-range-banner";
import { useJobMonitorScreenModel } from "./use-job-monitor-screen-model";

export type JobMonitorScreenProps = {
  dependencies: AdminDashboardDependencies;
};

const cardStyle = {
  padding: "20px",
  borderRadius: "22px",
  background: "rgba(255, 255, 255, 0.82)",
  border: "1px solid rgba(24, 33, 43, 0.08)",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
} as const;

function baseControlStyle() {
  return {
    width: "100%",
    minHeight: "40px",
    padding: "10px 12px",
    borderRadius: "14px",
    border: "1px solid rgba(148, 163, 184, 0.45)",
    background: "#fff",
    color: "#18212b",
    fontSize: "0.92rem",
  } as const;
}

function createNextSearchParams(formData: FormData, locale: "ko" | "en"): URLSearchParams {
  const params = new URLSearchParams();

  for (const key of [
    "from",
    "to",
    "jobType",
    "status",
    "userIdHash",
    "petIdHash",
    "linkedFileId",
    "linkedRecordId",
    "requestId",
    "correlationId",
    "release",
    "appVersion",
  ]) {
    const rawValue = formData.get(key);
    const value = typeof rawValue === "string" ? rawValue.trim() : "";

    if (value.length > 0) {
      params.set(key, value);
    }
  }

  return withAdminDashboardLocaleSearchParams(params, locale);
}

function formatJobScopeSummary(
  params: URLSearchParams,
  locale: "ko" | "en",
): string | undefined {
  return formatAdminDashboardSummary(locale, [
    { key: "request", value: params.get("requestId") },
    { key: "correlation", value: params.get("correlationId") },
    { key: "user", value: params.get("userIdHash") },
    { key: "release", value: params.get("release") },
    { key: "app", value: params.get("appVersion") },
  ]);
}

function buildJobStateMeta(
  locale: "ko" | "en",
  query: {
    timeRange: {
      from: string;
      to: string;
    };
  },
  scopeSummary: string | undefined,
) {
  return [
    buildAdminStateTimeRangeMeta(locale, query.timeRange.from, query.timeRange.to),
    buildAdminStateScopeMeta(locale, scopeSummary),
    buildAdminStateRouteMeta(locale, "/admin/jobs"),
  ].filter((item): item is NonNullable<typeof item> => item !== undefined);
}

export function JobMonitorScreen(props: JobMonitorScreenProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const refreshTick = useAdminAutoRefreshTick(props.dependencies.runtimeConfig);
  const effectiveSearchParams = useMemo(
    () => clearHiddenAdminListCursorSearchParams(searchParams),
    [searchParams],
  );
  const searchParamsKey = effectiveSearchParams.toString();
  const defaultTimeRangeRefreshSeed = resolveAdminLiveTimeRangeRefreshSeed(effectiveSearchParams, refreshTick);
  const defaultTimeRange = useMemo(
    () => createDefaultAdminListTimeRange(),
    [defaultTimeRangeRefreshSeed],
  );
  const query = useMemo(
    () => parseJobQueryParams(effectiveSearchParams, defaultTimeRange),
    [defaultTimeRange, effectiveSearchParams, searchParamsKey],
  );
  const explicitUpperBound = resolveExplicitAdminTimeRangeUpperBound(effectiveSearchParams);
  const scopeSummary = formatJobScopeSummary(effectiveSearchParams, locale);

  useEffect(() => {
    if (effectiveSearchParams.toString() === searchParams.toString()) {
      return;
    }

    setSearchParams(withAdminDashboardLocaleSearchParams(effectiveSearchParams, locale), {
      replace: true,
    });
  }, [effectiveSearchParams, locale, searchParams, setSearchParams]);
  const screenModelDependencies = useMemo(
    () => ({
      ...props.dependencies,
      locale,
      query,
      preserveExplicitUpperBound: explicitUpperBound !== undefined,
    }),
    [props.dependencies, locale, query, explicitUpperBound],
  );
  const state = useJobMonitorScreenModel(screenModelDependencies);
  const copy = {
    section: pickAdminDashboardLocaleText(locale, { en: "Jobs", ko: "작업" }),
    title: pickAdminDashboardLocaleText(locale, { en: "Job Monitor", ko: "작업 모니터" }),
    defaultScopeTitle: pickAdminDashboardLocaleText(locale, {
      en: "Default scope: full-history latest jobs",
      ko: "기본 범위: 전체 히스토리 최신 작업",
    }),
    defaultScopeBody: pickAdminDashboardLocaleText(locale, {
      en: "This screen opens with the latest 100 jobs across full history. Use the filters below only when you need to narrow by job type, status, user, request, correlation, or release.",
      ko: "이 화면은 전체 히스토리 기준 최신 작업 100건을 먼저 엽니다. 작업 유형, 상태, 사용자, 요청, 상관관계, 릴리스 기준으로 좁혀야 할 때만 아래 필터를 사용합니다.",
    }),
    env: pickAdminDashboardLocaleText(locale, { en: "env", ko: "환경" }),
    from: pickAdminDashboardLocaleText(locale, { en: "From", ko: "시작" }),
    to: pickAdminDashboardLocaleText(locale, { en: "To", ko: "종료" }),
    jobType: pickAdminDashboardLocaleText(locale, { en: "Job Type", ko: "작업 유형" }),
    status: pickAdminDashboardLocaleText(locale, { en: "Status", ko: "상태" }),
    user: pickAdminDashboardLocaleText(locale, { en: "User", ko: "사용자" }),
    pet: pickAdminDashboardLocaleText(locale, { en: "Pet", ko: "펫" }),
    file: pickAdminDashboardLocaleText(locale, { en: "File", ko: "파일" }),
    record: pickAdminDashboardLocaleText(locale, { en: "Record", ko: "기록" }),
    request: pickAdminDashboardLocaleText(locale, { en: "Request", ko: "요청" }),
    correlation: pickAdminDashboardLocaleText(locale, { en: "Correlation", ko: "상관관계" }),
    release: pickAdminDashboardLocaleText(locale, { en: "Release", ko: "릴리스" }),
    app: pickAdminDashboardLocaleText(locale, { en: "App", ko: "앱" }),
    apply: pickAdminDashboardLocaleText(locale, { en: "Apply filters", ko: "필터 적용" }),
    reset: pickAdminDashboardLocaleText(locale, { en: "Reset", ko: "초기화" }),
    loading: pickAdminDashboardLocaleText(locale, { en: "Loading jobs...", ko: "작업 데이터를 불러오는 중입니다..." }),
    deniedTitle: pickAdminDashboardLocaleText(locale, { en: "Jobs denied", ko: "작업 조회 권한 없음" }),
    deniedBody: pickAdminDashboardLocaleText(locale, { en: "Job monitor read permission is missing.", ko: "작업 모니터 조회 권한이 없습니다." }),
    failedTitle: pickAdminDashboardLocaleText(locale, { en: "Jobs failed", ko: "작업 조회 실패" }),
    emptyTitle: pickAdminDashboardLocaleText(locale, { en: "No jobs", ko: "작업 없음" }),
    emptyBody: pickAdminDashboardLocaleText(locale, {
      en: "No job row matches the current filter.",
      ko: "현재 필터 조건에 맞는 작업 행이 없습니다.",
    }),
    description: pickAdminDashboardLocaleText(locale, {
      en: "Read the current backlog hotspot first, then compare queued, processing, and failed async work before drilling into the affected request or user trace.",
      ko: "현재 backlog 핫스팟을 먼저 읽고, 영향을 받은 요청 또는 사용자 추적으로 내려가기 전에 대기, 처리 중, 실패 비동기 작업을 함께 비교합니다.",
    }),
    topHotspot: pickAdminDashboardLocaleText(locale, { en: "Top hotspot", ko: "상단 핫스팟" }),
    openHotspot: pickAdminDashboardLocaleText(locale, { en: "Open hotspot", ko: "핫스팟 열기" }),
    meta: pickAdminDashboardLocaleText(locale, { en: "Meta", ko: "조회 메타" }),
    generated: pickAdminDashboardLocaleText(locale, { en: "Generated", ko: "생성 시각" }),
    sourceStatus: pickAdminDashboardLocaleText(locale, { en: "Source status", ko: "소스 상태" }),
    total: pickAdminDashboardLocaleText(locale, { en: "Total", ko: "전체" }),
    queued: pickAdminDashboardLocaleText(locale, { en: "Queued", ko: "대기" }),
    processing: pickAdminDashboardLocaleText(locale, { en: "Processing", ko: "처리 중" }),
    failed: pickAdminDashboardLocaleText(locale, { en: "Failed", ko: "실패" }),
    backlogEvidenceTitle: pickAdminDashboardLocaleText(locale, { en: "Backlog evidence", ko: "백로그 증거" }),
    backlogEvidenceDesc: pickAdminDashboardLocaleText(locale, { en: "Compare queued, processing, and failed counts first, then use the table to follow the specific job, owner, and linked file or record.", ko: "먼저 대기, 처리 중, 실패 수를 비교하고, 그다음 표에서 구체적인 작업, 소유자, 연결된 파일 또는 기록을 추적합니다." }),
    jobId: pickAdminDashboardLocaleText(locale, { en: "Job ID", ko: "작업 ID" }),
    attempts: pickAdminDashboardLocaleText(locale, { en: "Attempts", ko: "시도 횟수" }),
    lastError: pickAdminDashboardLocaleText(locale, { en: "Last Error", ko: "마지막 오류" }),
    provider: pickAdminDashboardLocaleText(locale, { en: "Provider", ko: "제공자" }),
    duration: pickAdminDashboardLocaleText(locale, { en: "Duration", ko: "소요 시간" }),
    queuedAt: pickAdminDashboardLocaleText(locale, { en: "Queued", ko: "대기 시각" }),
    started: pickAdminDashboardLocaleText(locale, { en: "Started", ko: "시작 시각" }),
    finished: pickAdminDashboardLocaleText(locale, { en: "Finished", ko: "완료 시각" }),
    action: pickAdminDashboardLocaleText(locale, { en: "Action", ko: "동작" }),
    noRows: pickAdminDashboardLocaleText(locale, { en: "No rows", ko: "행 없음" }),
    noRowsBody: pickAdminDashboardLocaleText(locale, { en: "No job rows were returned.", ko: "조회된 작업 행이 없습니다." }),
  };

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setSearchParams(createNextSearchParams(new FormData(event.currentTarget), locale));
  }

  function handleResetToLiveRange(): void {
    setSearchParams(withAdminDashboardLocaleSearchParams(clearExplicitAdminTimeRangeSearchParams(searchParams), locale));
  }

  return (
    <section style={{ display: "grid", gap: "18px" }}>
      <header style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
            <h3 style={{ margin: "8px 0 0", fontSize: "1.5rem", letterSpacing: "-0.03em" }}>
              {copy.title}
            </h3>
            <p style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.6, maxWidth: "44rem" }}>
              {copy.description}
            </p>
            <AdminOperatorExplanationBlock
              screen="jobs"
              slot="jobs_top_hotspot"
              style={{ marginTop: "14px", maxWidth: "44rem" }}
            />
          </div>
          <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>
            {copy.env}: {props.dependencies.runtimeConfig.environmentName}
          </p>
        </div>
      </header>

      {explicitUpperBound ? (
        <AdminLiveRangeBanner
          explicitUpperBound={explicitUpperBound}
          locale={locale}
          onResetToLive={handleResetToLiveRange}
        />
      ) : null}

      <section style={cardStyle}>
        <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.defaultScopeTitle}</p>
        <p style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.6 }}>{copy.defaultScopeBody}</p>
      </section>

      <form onSubmit={handleFilterSubmit}>
        <AdminFilterBar
          items={[
            {
              id: "from",
              label: copy.from,
              control: <input defaultValue={searchParams.get("from") ?? ""} id="from" name="from" style={baseControlStyle()} />,
            },
            {
              id: "to",
              label: copy.to,
              control: <input defaultValue={searchParams.get("to") ?? ""} id="to" name="to" style={baseControlStyle()} />,
            },
            {
              id: "jobType",
              label: copy.jobType,
              control: <input defaultValue={searchParams.get("jobType") ?? ""} id="jobType" name="jobType" style={baseControlStyle()} />,
            },
            {
              id: "status",
              label: copy.status,
              control: <input defaultValue={searchParams.get("status") ?? ""} id="status" name="status" style={baseControlStyle()} />,
            },
            {
              id: "userIdHash",
              label: copy.user,
              control: <input defaultValue={searchParams.get("userIdHash") ?? ""} id="userIdHash" name="userIdHash" style={baseControlStyle()} />,
            },
            {
              id: "petIdHash",
              label: copy.pet,
              control: <input defaultValue={searchParams.get("petIdHash") ?? ""} id="petIdHash" name="petIdHash" style={baseControlStyle()} />,
            },
            {
              id: "linkedFileId",
              label: copy.file,
              control: <input defaultValue={searchParams.get("linkedFileId") ?? ""} id="linkedFileId" name="linkedFileId" style={baseControlStyle()} />,
            },
            {
              id: "linkedRecordId",
              label: copy.record,
              control: <input defaultValue={searchParams.get("linkedRecordId") ?? ""} id="linkedRecordId" name="linkedRecordId" style={baseControlStyle()} />,
            },
            {
              id: "requestId",
              label: copy.request,
              control: <input defaultValue={searchParams.get("requestId") ?? ""} id="requestId" name="requestId" style={baseControlStyle()} />,
            },
            {
              id: "correlationId",
              label: copy.correlation,
              control: <input defaultValue={searchParams.get("correlationId") ?? ""} id="correlationId" name="correlationId" style={baseControlStyle()} />,
            },
            {
              id: "release",
              label: copy.release,
              control: <input defaultValue={searchParams.get("release") ?? ""} id="release" name="release" style={baseControlStyle()} />,
            },
            {
              id: "appVersion",
              label: copy.app,
              control: <input defaultValue={searchParams.get("appVersion") ?? ""} id="appVersion" name="appVersion" style={baseControlStyle()} />,
            },
          ]}
          actions={
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                style={{
                  minHeight: "40px",
                  padding: "0 14px",
                  borderRadius: "999px",
                  border: "none",
                  background: "#0f766e",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
                type="submit"
              >
                {copy.apply}
              </button>
              <AdminLocalizedLink
                to="/admin/jobs"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  minHeight: "40px",
                  padding: "0 14px",
                  borderRadius: "999px",
                  border: "1px solid rgba(148, 163, 184, 0.45)",
                  color: "#18212b",
                  textDecoration: "none",
                  fontWeight: 700,
                }}
              >
                {copy.reset}
              </AdminLocalizedLink>
            </div>
          }
        />
      </form>

      {state.status === "loading" || state.status === "idle" ? (
        <section style={cardStyle}>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
          <p style={{ margin: "10px 0 0", color: "#475569" }}>{copy.loading}</p>
        </section>
      ) : null}

      {state.status === "permission_denied" ? (
        <AdminErrorState
          title={copy.deniedTitle}
          body={copy.deniedBody}
          guidance={{
            screen: "jobs",
            slot: "shared_error_state_guidance",
          }}
          meta={buildJobStateMeta(locale, query, scopeSummary)}
          requestId={state.error.requestId}
          tone="permission_denied"
        />
      ) : null}

      {state.status === "error" ? (
        <AdminErrorState
          title={copy.failedTitle}
          body={state.error.message}
          guidance={{
            screen: "jobs",
            slot: "shared_error_state_guidance",
          }}
          meta={buildJobStateMeta(locale, query, scopeSummary)}
          requestId={state.error.requestId}
          tone="error"
        />
      ) : null}

      {state.status === "empty" ? (
        <AdminEmptyState
          title={copy.emptyTitle}
          body={copy.emptyBody}
          guidance={{
            screen: "jobs",
            slot: "shared_empty_state_guidance",
          }}
          meta={buildJobStateMeta(locale, query, scopeSummary)}
        />
      ) : null}

      {state.status === "ready" || state.status === "partial" || state.status === "stale" ? (
        <>
          <section
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.2fr) minmax(260px, 0.8fr)",
              gap: "16px",
            }}
          >
            <article
              style={{
                ...cardStyle,
                display: "grid",
                gap: "10px",
              }}
            >
              <p style={{ margin: 0, color: "#64748b", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                {copy.topHotspot}
              </p>
              <p style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.03em" }}>
                {state.viewModel.hotspot.headline}
              </p>
              <p style={{ margin: 0, color: "#475569", fontSize: "0.94rem", lineHeight: 1.6 }}>
                {state.viewModel.hotspot.detail}
              </p>
              <AdminLocalizedLink to={state.viewModel.hotspot.drilldownTo} style={{ fontWeight: 700 }}>
                {copy.openHotspot}
              </AdminLocalizedLink>
            </article>

            <section
              style={{
                ...cardStyle,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <div>
                <p style={{ margin: 0, color: "#64748b", fontSize: "0.82rem", fontWeight: 700 }}>
                  {copy.meta}
                </p>
                <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: "0.88rem" }}>
                  {copy.generated} {state.viewModel.generatedAtLabel}
                </p>
                <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: "0.88rem" }}>
                  {copy.request} {state.viewModel.requestId}
                </p>
              </div>
              <AdminStatusBadge value={toAdminFreshnessBadgeValue(state.viewModel.freshness)} />
            </section>
          </section>

          <section
            style={{
              display: "grid",
              gap: "12px",
            }}
          >
            <article style={cardStyle}>
              <div style={{ display: "grid", gap: "10px" }}>
                <p
                  style={{
                    margin: 0,
                    color: "#64748b",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {copy.sourceStatus}
                </p>
                <AdminSourceStatusGrid
                  items={state.viewModel.sourceFreshness}
                  statusLabel={copy.sourceStatus}
                />
              </div>
            </article>
            {state.viewModel.aiRuntime ? (
              <AdminAiRuntimeSummaryStrip
                ariaLabel={state.viewModel.aiRuntime.title}
                items={state.viewModel.aiRuntime.items}
                sourceGapLabel={state.viewModel.aiRuntime.sourceGapLabel}
                title={state.viewModel.aiRuntime.title}
              />
            ) : null}
            {state.viewModel.observability ? (
              <AdminAggregateSummaryStrip
                ariaLabel={state.viewModel.observability.title}
                items={state.viewModel.observability.items}
                sourceGapLabel={state.viewModel.observability.sourceGapLabel}
                title={state.viewModel.observability.title}
              />
            ) : null}
            {state.viewModel.rowSnapshot ? (
              <AdminAggregateSummaryStrip
                ariaLabel={state.viewModel.rowSnapshot.title}
                items={state.viewModel.rowSnapshot.items}
                sourceGapLabel={state.viewModel.rowSnapshot.sourceGapLabel}
                title={state.viewModel.rowSnapshot.title}
              />
            ) : null}
            <div style={{ display: "grid", gap: "8px" }}>
              <div style={{ display: "grid", gap: "6px" }}>
                <p
                  style={{
                    margin: 0,
                    color: "#64748b",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {copy.backlogEvidenceTitle}
                </p>
                <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>
                  {copy.backlogEvidenceDesc}
                </p>
              </div>
              <AdminOperatorExplanationBlock
                screen="jobs"
                slot="jobs_table_reading_order"
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "14px",
              }}
            >
              {[
                [copy.total, state.viewModel.backlogSummary.totalCountLabel],
                [copy.queued, state.viewModel.backlogSummary.queuedCountLabel],
                [copy.processing, state.viewModel.backlogSummary.processingCountLabel],
                [copy.failed, state.viewModel.backlogSummary.failedCountLabel],
              ].map(([label, value]) => (
                <article key={label} style={cardStyle}>
                  <p style={{ margin: 0, color: "#64748b", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    {label}
                  </p>
                  <p style={{ margin: "10px 0 0", fontSize: "1.45rem", fontWeight: 700, letterSpacing: "-0.03em" }}>
                    {value}
                  </p>
                </article>
              ))}
            </div>

            {state.viewModel.accountDeletionSeverity ? (
              <AdminLocalizedLink
                to={state.viewModel.accountDeletionSeverity.drilldownTo}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                <article style={cardStyle}>
                  <p style={{ margin: 0, color: "#64748b", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    {state.viewModel.accountDeletionSeverity.label}
                  </p>
                  <p style={{ margin: "10px 0 0", fontSize: "1.35rem", fontWeight: 700, letterSpacing: "-0.03em" }}>
                    {state.viewModel.accountDeletionSeverity.headline}
                  </p>
                  <p style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.6 }}>
                    {state.viewModel.accountDeletionSeverity.detail}
                  </p>
                </article>
              </AdminLocalizedLink>
            ) : null}
          </section>

          <AdminDataTable
            columns={[
              { id: "jobId", label: copy.jobId, sortValue: (row) => row.jobId, renderCell: (row) => row.jobId },
              {
                id: "jobType",
                label: copy.jobType,
                sortValue: (row) => row.jobType,
                renderCell: (row) => (
                  <div>
                    <div style={{ fontWeight: 700 }}>{row.jobTypeLabel}</div>
                    <div style={{ color: "#64748b", marginTop: "4px", fontSize: "0.84rem" }}>{row.priorityLabel}</div>
                  </div>
                ),
              },
              { id: "ownerUserIdHash", label: copy.user, renderCell: (row) => row.ownerUserIdHashLabel },
              { id: "petIdHash", label: copy.pet, renderCell: (row) => row.petIdHashLabel },
              { id: "status", label: copy.status, sortValue: (row) => row.status, renderCell: (row) => row.statusLabel },
              {
                id: "attemptCount",
                label: copy.attempts,
                sortValue: (row) => row.attemptCount,
                defaultSortDirection: "desc",
                renderCell: (row) => row.attemptCountLabel,
                align: "right",
              },
              { id: "lastErrorCode", label: copy.lastError, renderCell: (row) => row.lastErrorCodeLabel },
              { id: "provider", label: copy.provider, renderCell: (row) => row.providerLabel },
              { id: "duration", label: copy.duration, renderCell: (row) => row.durationLabel },
              {
                id: "queuedAt",
                label: copy.queuedAt,
                sortValue: (row) => row.queuedAt,
                defaultSortDirection: "desc",
                renderCell: (row) => row.queuedAtLabel,
              },
              { id: "startedAt", label: copy.started, renderCell: (row) => row.startedAtLabel },
              { id: "finishedAt", label: copy.finished, renderCell: (row) => row.finishedAtLabel },
              { id: "linkedFileId", label: copy.file, renderCell: (row) => row.linkedFileIdLabel },
              { id: "linkedRecordId", label: copy.record, renderCell: (row) => row.linkedRecordIdLabel },
              {
                id: "actionSlot",
                label: copy.action,
                renderCell: (row) => (
                  <button
                    disabled
                    style={{
                      minHeight: "32px",
                      padding: "0 10px",
                      borderRadius: "999px",
                      border: "1px solid rgba(148, 163, 184, 0.45)",
                      background: "rgba(241, 245, 249, 0.9)",
                      color: "#64748b",
                      fontWeight: 700,
                      cursor: "not-allowed",
                    }}
                    type="button"
                  >
                    {row.actionSlotLabel}
                  </button>
                ),
              },
            ]}
            emptyState={<AdminEmptyState title={copy.noRows} body={copy.noRowsBody} />}
            getRowId={(row) => row.jobId}
            rows={state.viewModel.rows}
          />
        </>
      ) : null}
    </section>
  );
}
